import { useEffect, useMemo, useState, type FormEvent } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { api, callableErrorMessage } from "../lib/api";
import { usePlatformTenantsSummary, type TenantSummary } from "../lib/platformStats";
import { setImpersonatedTenant } from "../lib/impersonation";

export function PlatformHome() {
  const { user, refreshClaims, claims } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [showInviteAdmin, setShowInviteAdmin] = useState(false);
  const [showAdminsModal, setShowAdminsModal] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [bootstrapBusy, setBootstrapBusy] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TenantSummary | null>(null);

  const { tenants, loading } = usePlatformTenantsSummary(showDeleted);

  const kpis = useMemo(() => {
    const active = tenants.filter((t) => t.status === "active");
    const paying = tenants.filter((t) => t.subscriptionStatus === "authorized");
    return {
      tenantsActive: active.length,
      tenantsTotal: tenants.length,
      paying: paying.length,
      mrr: paying.reduce((acc, t) => acc + (t.subscriptionAmount ?? 0), 0),
      customers: tenants.reduce((acc, t) => acc + t.customers, 0),
      todayPurchases: tenants.reduce((acc, t) => acc + t.todayPurchases, 0)
    };
  }, [tenants]);

  const onBootstrap = async () => {
    setBootstrapBusy(true);
    setBootstrapError(null);
    try {
      await api.bootstrapPlatformAdmin();
      await refreshClaims();
    } catch (err) {
      setBootstrapError(callableErrorMessage(err));
    } finally {
      setBootstrapBusy(false);
    }
  };

  if (claims.role !== "platform_admin") {
    return (
      <main className="min-h-dvh bg-cream">
        <div className="container-page flex flex-col gap-6">
          <header>
            <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Fideliza · Platform</p>
            <h1 className="mt-1 font-display text-2xl font-semibold text-leaf-950">
              Bootstrap platform-admin
            </h1>
          </header>
          <section className="card flex flex-col gap-3">
            <p className="text-sm text-leaf-800">
              Tu cuenta <span className="font-semibold">{user?.email}</span> coincide con el email
              configurado para ser el primer platform-admin. Confirma para activar el rol.
            </p>
            <p className="font-mono text-xs text-leaf-600">UID: {user?.uid}</p>
            {bootstrapError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {bootstrapError}
              </div>
            )}
            <button onClick={onBootstrap} disabled={bootstrapBusy} className="btn-primary">
              {bootstrapBusy ? "Activando…" : "Activar platform-admin"}
            </button>
            <button onClick={() => signOut(auth)} className="btn-ghost text-sm">
              Cerrar sesión
            </button>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-dvh bg-cream">
      <div className="container-page flex flex-col gap-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Fideliza · Platform</p>
            <h1 className="mt-1 font-display text-2xl font-semibold text-leaf-950">
              Panel admin
            </h1>
            <p className="text-xs text-leaf-600">Hola {user?.email}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowAdminsModal(true)}
              className="btn-ghost px-3 py-2 text-sm"
            >
              Admins Fideliza
            </button>
            <button onClick={() => signOut(auth)} className="btn-ghost px-3 py-2 text-sm">
              Salir
            </button>
          </div>
        </header>

        <PlatformKpis kpis={kpis} />

        <section className="card flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-leaf-800">
              {tenants.length} {tenants.length === 1 ? "tenant" : "tenants"}
              {showDeleted ? " (incluye eliminados)" : ""}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowDeleted((s) => !s)}
                className="btn-ghost px-3 py-1.5 text-xs"
              >
                {showDeleted ? "Ocultar eliminados" : "Mostrar eliminados"}
              </button>
              <button
                onClick={() => setShowInviteAdmin(true)}
                className="btn-ghost px-3 py-1.5 text-xs"
              >
                + Invitar admin
              </button>
              <button
                onClick={() => setShowCreate((s) => !s)}
                className="btn-primary px-4 py-1.5 text-xs"
              >
                {showCreate ? "Cancelar" : "+ Nuevo tenant"}
              </button>
            </div>
          </div>
          {showCreate && <CreateTenantForm onDone={() => setShowCreate(false)} />}
        </section>

        {loading && (
          <p className="text-center text-sm text-leaf-600">Cargando tenants…</p>
        )}

        <section className="grid gap-3 sm:grid-cols-2">
          {tenants.map((t) => (
            <TenantSummaryCard
              key={t.tenantId}
              tenant={t}
              onDelete={() => setDeleteTarget(t)}
              onRestore={async () => {
                try {
                  await api.restoreTenant({ tenantId: t.tenantId });
                } catch (err) {
                  alert(callableErrorMessage(err));
                }
              }}
            />
          ))}
          {tenants.length === 0 && !loading && (
            <p className="col-span-full px-2 text-sm text-leaf-700">
              Aún no hay tenants. Crea el primero con "+ Nuevo tenant".
            </p>
          )}
        </section>
      </div>

      {showInviteAdmin && (
        <InvitePlatformAdminModal onClose={() => setShowInviteAdmin(false)} />
      )}
      {showAdminsModal && (
        <PlatformAdminsModal onClose={() => setShowAdminsModal(false)} />
      )}
      {deleteTarget && (
        <DeleteTenantModal
          tenant={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </main>
  );
}

// ============================================================
// KPIs
// ============================================================

function PlatformKpis({
  kpis
}: {
  kpis: {
    tenantsActive: number;
    tenantsTotal: number;
    paying: number;
    mrr: number;
    customers: number;
    todayPurchases: number;
  };
}) {
  const fmt = (n: number) =>
    new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

  return (
    <section className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      <Kpi label="Tenants activos" value={kpis.tenantsActive} sub={`de ${kpis.tenantsTotal}`} />
      <Kpi label="Pagando" value={kpis.paying} sub="con sub authorized" />
      <Kpi label="MRR estimado" value={fmt(kpis.mrr)} />
      <Kpi label="Clientes totales" value={kpis.customers} />
      <Kpi label="Boletas hoy" value={kpis.todayPurchases} highlight={kpis.todayPurchases > 0} />
    </section>
  );
}

function Kpi({
  label,
  value,
  sub,
  highlight = false
}: {
  label: string;
  value: number | string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl p-4 ring-1 ${
        highlight ? "bg-amber-50 ring-amber-200" : "bg-white ring-leaf-100"
      }`}
    >
      <p
        className={`text-[10px] uppercase tracking-wider ${
          highlight ? "text-amber-800" : "text-leaf-600"
        }`}
      >
        {label}
      </p>
      <p
        className={`mt-1 font-display text-xl font-semibold ${
          highlight ? "text-amber-950" : "text-leaf-950"
        }`}
      >
        {value}
      </p>
      {sub && <p className="text-[10px] text-leaf-500">{sub}</p>}
    </div>
  );
}

// ============================================================
// Tenant Summary Card (con color del tenant)
// ============================================================

function TenantSummaryCard({
  tenant,
  onDelete,
  onRestore
}: {
  tenant: TenantSummary;
  onDelete: () => void;
  onRestore: () => void;
}) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const isDeleted = tenant.status === "deleted";
  const fmt = (n: number) =>
    new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);

  return (
    <article
      className="overflow-hidden rounded-3xl border bg-white"
      style={{
        borderColor: isDeleted ? "#d1d5db" : tenant.themePrimary + "33",
        opacity: isDeleted ? 0.6 : 1
      }}
    >
      <div
        className="h-2"
        style={{ backgroundColor: isDeleted ? "#9ca3af" : tenant.themePrimary }}
      />
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          {tenant.logoUrl ? (
            <img
              src={tenant.logoUrl}
              alt={tenant.name}
              className="h-12 w-12 shrink-0 rounded-full object-cover ring-2"
              style={{ boxShadow: `0 0 0 2px ${tenant.themePrimary}` }}
            />
          ) : (
            <div
              className="grid h-12 w-12 shrink-0 place-items-center rounded-full font-display text-lg font-semibold text-white"
              style={{ backgroundColor: tenant.themePrimary }}
            >
              {tenant.name.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-base font-semibold text-leaf-950">
              {tenant.name}
            </p>
            <p className="font-mono text-[11px] text-leaf-600">/{tenant.tenantId}</p>
          </div>
          <StatusBadge tenant={tenant} />
        </div>

        <div className="flex flex-wrap gap-2 text-[11px]">
          <Badge
            label={`Verif: ${tenant.verificationStatus ?? "n/a"}`}
            tone={
              tenant.verificationStatus === "verified"
                ? "ok"
                : tenant.verificationStatus === "rejected"
                ? "bad"
                : "warn"
            }
          />
          <Badge
            label={
              tenant.subscriptionStatus === "authorized"
                ? `Sub activa ${tenant.subscriptionAmount ? `· ${fmt(tenant.subscriptionAmount)}` : ""}`
                : tenant.subscriptionStatus === "pending"
                ? "Sub pending"
                : tenant.subscriptionStatus === "cancelled"
                ? "Sub cancelada"
                : "Sin sub"
            }
            tone={
              tenant.subscriptionStatus === "authorized"
                ? "ok"
                : tenant.subscriptionStatus === "pending"
                ? "warn"
                : "neutral"
            }
          />
        </div>

        <div className="grid grid-cols-4 gap-2 text-center">
          <Stat label="Clientes" value={tenant.customers} />
          <Stat label="Embaj." value={tenant.ambassadors} />
          <Stat label="Admins" value={tenant.tenantAdmins} />
          <Stat
            label="Hoy"
            value={tenant.todayPurchases}
            highlight={tenant.todayPurchases > 0}
          />
        </div>

        <div className="flex flex-wrap gap-2 border-t border-leaf-100 pt-3">
          {tenant.verificationStatus === "pending_review" && (
            <button
              onClick={() => setReviewOpen(true)}
              className="rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-200"
            >
              Revisar signup
            </button>
          )}
          <a
            href={`/preview-cliente?tenant=${encodeURIComponent(tenant.tenantId)}`}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border border-leaf-200 bg-white px-3 py-1 text-xs text-leaf-700 hover:bg-leaf-50"
          >
            Ver como cliente final
          </a>
          <button
            onClick={() => {
              setImpersonatedTenant(tenant.tenantId);
              window.location.href = "/admin";
            }}
            className="rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-medium text-purple-700 hover:bg-purple-100"
          >
            Ver panel del tenant
          </button>
          {!isDeleted && (
            <button
              onClick={onDelete}
              className="ml-auto rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs text-red-700 hover:bg-red-100"
            >
              Eliminar
            </button>
          )}
          {isDeleted && (
            <button
              onClick={onRestore}
              className="ml-auto rounded-full border border-leaf-200 bg-leaf-50 px-3 py-1 text-xs text-leaf-700 hover:bg-leaf-100"
            >
              Restaurar
            </button>
          )}
        </div>
      </div>
      {reviewOpen && (
        <ReviewSignupModal tenant={tenant} onClose={() => setReviewOpen(false)} />
      )}
    </article>
  );
}

function ReviewSignupModal({ tenant, onClose }: { tenant: TenantSummary; onClose: () => void }) {
  const [busy, setBusy] = useState<"verified" | "rejected" | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [signup, setSignup] = useState<{
    legal?: { legalName?: string; taxId?: string; taxIdFormat?: string; personType?: string };
    business?: { type?: string; address?: string; city?: string; instagram?: string; website?: string; locationsRange?: string };
    owner?: { displayName?: string; email?: string; phone?: string; role?: string };
    refSource?: string | null;
    createdAt?: { toDate?: () => Date };
  } | null>(null);

  useEffect(() => {
    import("firebase/firestore").then(({ doc, getDoc }) => {
      import("../lib/firebase").then(({ db }) => {
        getDoc(doc(db, "tenants", tenant.tenantId)).then((s) => setSignup(s.data() as never));
      });
    });
  }, [tenant.tenantId]);

  const submit = async (decision: "verified" | "rejected") => {
    if (decision === "rejected" && note.trim().length < 4) {
      setError("Para rechazar necesitas escribir un motivo (mínimo 4 caracteres).");
      return;
    }
    setBusy(decision);
    setError(null);
    try {
      await api.reviewTenantVerification({
        tenantId: tenant.tenantId,
        decision,
        note: note.trim() || undefined
      });
      onClose();
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-leaf-600">Revisar signup</p>
            <h2 className="mt-1 font-display text-xl font-semibold text-leaf-950">{tenant.name}</h2>
          </div>
          <button onClick={onClose} className="text-leaf-500 hover:text-leaf-700">✕</button>
        </div>

        {!signup ? (
          <p className="mt-4 text-sm text-leaf-600">Cargando datos…</p>
        ) : (
          <div className="mt-4 flex flex-col gap-3 text-sm">
            <Field label="Razón social" value={signup.legal?.legalName ?? "—"} />
            <Field label="RUT" value={signup.legal?.taxId ?? "—"} hint={signup.legal?.personType} />
            <Field label="Tipo de negocio" value={signup.business?.type ?? "—"} />
            <Field
              label="Dirección"
              value={`${signup.business?.address ?? "—"}${signup.business?.city ? `, ${signup.business.city}` : ""}`}
            />
            <Field label="Locales" value={signup.business?.locationsRange ?? "—"} />
            {signup.business?.instagram && (
              <Field label="Instagram" value={signup.business.instagram} />
            )}
            {signup.business?.website && (
              <Field label="Web" value={signup.business.website} />
            )}
            <div className="my-2 h-px bg-leaf-100" />
            <Field label="Dueño" value={signup.owner?.displayName ?? "—"} hint={signup.owner?.role} />
            <Field label="Email" value={signup.owner?.email ?? "—"} />
            {signup.owner?.phone && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs uppercase tracking-wider text-leaf-600">WhatsApp</span>
                <a
                  href={`https://wa.me/${signup.owner.phone.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-green-600 px-3 py-1 text-xs font-medium text-white"
                >
                  {signup.owner.phone} →
                </a>
              </div>
            )}
            <Field
              label="Vino por"
              value={signup.refSource ?? "Sin ref (directo / orgánico)"}
            />
          </div>
        )}

        <label className="mt-4 flex flex-col gap-1 text-xs text-leaf-700">
          Nota (opcional para aprobar, obligatoria para rechazar)
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Ej: Verificado SII / WhatsApp confirmado / RUT no existe"
            className="input text-sm"
          />
        </label>

        {error && (
          <p className="mt-2 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-red-200">
            {error}
          </p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={() => submit("rejected")}
            disabled={busy !== null}
            className="rounded-full border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-800 hover:bg-red-100 disabled:opacity-50"
          >
            {busy === "rejected" ? "Procesando…" : "Rechazar"}
          </button>
          <button
            onClick={() => submit("verified")}
            disabled={busy !== null}
            className="rounded-full bg-leaf-700 px-3 py-2 text-sm font-semibold text-white hover:bg-leaf-800 disabled:opacity-50"
          >
            {busy === "verified" ? "Aprobando…" : "Aprobar"}
          </button>
        </div>

        <p className="mt-3 text-[11px] text-leaf-500">
          El dueño recibe email automático con confirmación + link a su panel.
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-leaf-600">
        {label}
        {hint && <span className="ml-1 text-leaf-500">· {hint}</span>}
      </p>
      <p className="text-sm font-medium text-leaf-900">{value}</p>
    </div>
  );
}

function StatusBadge({ tenant }: { tenant: TenantSummary }) {
  const map: Record<TenantSummary["status"], { label: string; cls: string }> = {
    active: { label: "Activo", cls: "bg-leaf-100 text-leaf-800" },
    suspended: { label: "Suspendido", cls: "bg-amber-100 text-amber-800" },
    deleted: { label: "Eliminado", cls: "bg-gray-200 text-gray-700" }
  };
  const v = map[tenant.status] ?? map.active;
  return (
    <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] uppercase tracking-wider ${v.cls}`}>
      {v.label}
    </span>
  );
}

function Badge({
  label,
  tone
}: {
  label: string;
  tone: "ok" | "warn" | "bad" | "neutral";
}) {
  const cls: Record<typeof tone, string> = {
    ok: "bg-leaf-100 text-leaf-800",
    warn: "bg-amber-100 text-amber-800",
    bad: "bg-red-100 text-red-700",
    neutral: "bg-gray-100 text-gray-700"
  } as const;
  return (
    <span className={`rounded-full px-2 py-0.5 ${cls[tone]}`}>{label}</span>
  );
}

function Stat({
  label,
  value,
  highlight = false
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-xl p-2 ${highlight ? "bg-amber-50" : "bg-leaf-50/60"}`}>
      <p className={`font-display text-base font-semibold ${highlight ? "text-amber-900" : "text-leaf-950"}`}>
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-leaf-600">{label}</p>
    </div>
  );
}

// ============================================================
// Delete Tenant Modal
// ============================================================

function DeleteTenantModal({
  tenant,
  onClose
}: {
  tenant: TenantSummary;
  onClose: () => void;
}) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.softDeleteTenant({ tenantId: tenant.tenantId, confirmSlug: confirm });
      onClose();
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="font-display text-xl font-semibold text-leaf-950">
        Eliminar <span className="font-mono">{tenant.tenantId}</span>
      </h2>
      <p className="mt-2 text-sm text-leaf-700">
        Esto marca el tenant como <strong>eliminado</strong> (soft delete). La data queda en
        Firestore por si necesitas restaurarlo. Para borrar definitivo se requiere script
        manual.
      </p>
      <p className="mt-3 text-sm text-leaf-800">
        Escribi <code className="font-mono text-red-700">{tenant.tenantId}</code> para confirmar:
      </p>
      <form onSubmit={submit} className="mt-2 flex flex-col gap-3">
        <input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoFocus
          className="input font-mono"
          placeholder={tenant.tenantId}
        />
        {error && (
          <div className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-sm">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || confirm !== tenant.tenantId}
            className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? "Eliminando…" : "Eliminar"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================
// Invite Platform Admin Modal
// ============================================================

function InvitePlatformAdminModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.invitePlatformAdmin({ email, note: note.trim() || undefined });
      setSent(true);
      setTimeout(onClose, 1800);
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="font-display text-xl font-semibold text-leaf-950">
        Invitar admin Fideliza
      </h2>
      <p className="mt-2 text-sm text-leaf-700">
        Le da acceso al panel platform (ver todos los tenants, KPIs globales, gestion).
        NO es un tenant_admin, es admin de toda la plataforma.
      </p>
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-leaf-700">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            placeholder="admin@fideliza.cl"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-leaf-700">Mensaje (opcional)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="input h-20"
            placeholder="Te invito a ayudarme a gestionar Fideliza..."
          />
        </label>
        {error && (
          <div className="rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}
        {sent && (
          <div className="rounded-xl bg-leaf-50 px-3 py-2 text-xs text-leaf-800">
            Invitacion enviada ✓
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-sm">
            Cancelar
          </button>
          <button type="submit" disabled={busy || sent} className="btn-primary px-4 py-2 text-sm">
            {busy ? "Enviando…" : "Enviar invitacion"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ============================================================
// Platform Admins Modal (list + revoke)
// ============================================================

function PlatformAdminsModal({ onClose }: { onClose: () => void }) {
  const [admins, setAdmins] = useState<
    Array<{ uid: string; email: string | null; displayName: string | null; lastSignIn: string | null; isSelf: boolean }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.listPlatformAdmins();
      setAdmins(res.data.admins);
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revoke = async (uid: string) => {
    if (!confirm("¿Quitar el rol platform_admin a este usuario?")) return;
    try {
      await api.revokePlatformAdmin({ uid });
      await load();
    } catch (err) {
      alert(callableErrorMessage(err));
    }
  };

  return (
    <Modal onClose={onClose}>
      <h2 className="font-display text-xl font-semibold text-leaf-950">Admins Fideliza</h2>
      <p className="mt-2 text-sm text-leaf-700">
        Lista de usuarios con rol <code>platform_admin</code>.
      </p>
      {error && (
        <div className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
      )}
      {loading ? (
        <p className="mt-3 text-sm text-leaf-600">Cargando…</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {admins.map((a) => (
            <li
              key={a.uid}
              className="flex items-center justify-between gap-2 rounded-2xl border border-leaf-100 bg-leaf-50/50 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-leaf-950">{a.email ?? "(sin email)"}</p>
                <p className="font-mono text-[10px] text-leaf-600 truncate">{a.uid}</p>
                {a.lastSignIn && (
                  <p className="text-[10px] text-leaf-500">
                    Ultimo login: {new Date(a.lastSignIn).toLocaleDateString("es-CL")}
                  </p>
                )}
              </div>
              {a.isSelf ? (
                <span className="rounded-full bg-leaf-100 px-2 py-1 text-[10px] uppercase text-leaf-800">
                  Tú
                </span>
              ) : (
                <button
                  onClick={() => revoke(a.uid)}
                  className="rounded-full border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
                >
                  Quitar
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex justify-end">
        <button onClick={onClose} className="btn-primary px-4 py-2 text-sm">
          Cerrar
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Modal wrapper
// ============================================================

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-leaf-950/30 px-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ============================================================
// Create Tenant Form (igual que antes)
// ============================================================

function CreateTenantForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [primary, setPrimary] = useState("#4a6921");
  const [adminEmail, setAdminEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.createTenant({
        slug,
        name,
        theme: { primary },
        initialAdminEmail: adminEmail || undefined
      });
      onDone();
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 border-t border-leaf-100 pt-4">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Nombre del comercio</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          minLength={2}
          placeholder="MatchaLab"
          className="input"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Slug (URL)</span>
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          required
          pattern="[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?"
          placeholder="matchalab"
          className="input font-mono lowercase"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Color primario</span>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            className="h-12 w-16 cursor-pointer rounded-xl border-2 border-leaf-100"
          />
          <input
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            pattern="#[0-9a-fA-F]{6}"
            className="input flex-1 font-mono"
          />
        </div>
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-leaf-700">Email del tenant-admin (opcional)</span>
        <input
          type="email"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          placeholder="dueno@comercio.cl"
          className="input"
        />
      </label>
      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      <button type="submit" disabled={busy} className="btn-primary">
        {busy ? "Creando…" : "Crear tenant"}
      </button>
    </form>
  );
}
